import { Segment } from "@/components/keyword-bank/KeywordBankContentV2";
import type { SortDirection } from "@/components/shared/SortableTableHead";
import { LIFECYCLE_SORT_WEIGHT, lifecycleStateOf } from "@/constants/product";
import type { ProductListItem } from "@/types";

/**
 * AUT-363: where a products-list row's "View all" goes, relative to `/products/:productId`.
 * Both links used to call the row's own navigate, which lands on the bare `/products/:id` --
 * an index route redirecting to seo-insights (App.tsx) -- so a link sitting under "2 running"
 * or "3 themes" opened SEO Strategy instead of the thing it had just counted.
 */
export const AB_TESTS_TAB_PATH = "/ab-tests";

export const MASTER_LIST_TAB_PATH = `/keywords?segment=${Segment.MASTER_LIST}`;

export type CatalogSortField = "name" | "price" | "rating";

export type ProductSortField =
  | "name"
  | "asin"
  | "rating"
  | "reviews"
  | "price"
  | "visibility"
  | "abTests"
  | "themes"
  | "lifecycle"
  | "recentActivity";

export const ACTIVITY_LABELS: Record<string, string> = {
  ab_test_started: "A/B test started",
  ab_test_completed: "A/B test completed",
  theme_created: "Theme created",
  scorecard_completed: "Amazon AI Score completed",
  landscape_completed: "Landscape completed",
  seo_insight_generated: "SEO insight generated",
};

export enum ViewMode {
  CARD = "card",
  TABLE = "table",
}

export enum ProductsTab {
  HERO = "hero",
  CATALOG = "catalog",
}

export const VIEW_MODE_STORAGE_KEY = "products.viewMode";

export function gradeColor(grade: string | null | undefined): string {
  if (!grade) return "text-muted-foreground";
  if (grade.startsWith("A")) return "text-green-600";
  if (grade.startsWith("B")) return "text-blue-600";
  if (grade.startsWith("C")) return "text-yellow-600";
  return "text-red-600";
}

export function sortProducts(
  products: ProductListItem[],
  field: ProductSortField | null,
  direction: SortDirection,
): ProductListItem[] {
  if (!field) return products;
  return [...products].sort((a, b) => {
    let av: number | string = 0;
    let bv: number | string = 0;
    switch (field) {
      case "name":
        av = a.name;
        bv = b.name;
        break;
      case "asin":
        av = a.asin;
        bv = b.asin;
        break;
      case "rating":
        av = Number(a.rating ?? 0);
        bv = Number(b.rating ?? 0);
        break;
      case "reviews":
        av = a.reviews ?? 0;
        bv = b.reviews ?? 0;
        break;
      case "price":
        av = Number(a.price ?? 0);
        bv = Number(b.price ?? 0);
        break;
      case "visibility":
        av = a.visibility_score ?? 0;
        bv = b.visibility_score ?? 0;
        break;
      case "abTests":
        av = a.ab_tests_running ?? 0;
        bv = b.ab_tests_running ?? 0;
        break;
      case "themes":
        av = a.themes_count ?? 0;
        bv = b.themes_count ?? 0;
        break;
      case "lifecycle":
        av = LIFECYCLE_SORT_WEIGHT[lifecycleStateOf(a.lifecycle_state)];
        bv = LIFECYCLE_SORT_WEIGHT[lifecycleStateOf(b.lifecycle_state)];
        break;
      case "recentActivity":
        av = a.recent_activity?.timestamp ? new Date(a.recent_activity.timestamp).getTime() : 0;
        bv = b.recent_activity?.timestamp ? new Date(b.recent_activity.timestamp).getTime() : 0;
        break;
    }
    if (typeof av === "number" && typeof bv === "number") {
      return direction === "asc" ? av - bv : bv - av;
    }
    return direction === "asc"
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });
}

export function filterProducts(
  products: ProductListItem[],
  search: string,
): ProductListItem[] {
  const q = search.trim().toLowerCase();
  if (!q) return products;
  return products.filter(
    (p) =>
      p.name.toLowerCase().includes(q) ||
      p.asin.toLowerCase().includes(q),
  );
}

/**
 * The Lifecycle State filter group, which is multi-select: an empty selection
 * means "All", so it filters nothing. `selected` is `string[]` because that is
 * what `SearchFilterBar` hands back; a value outside the three states simply
 * matches no row.
 */
export function filterProductsByLifecycle(
  products: ProductListItem[],
  selected: string[],
): ProductListItem[] {
  if (selected.length === 0) return products;
  return products.filter((p) => selected.includes(lifecycleStateOf(p.lifecycle_state)));
}
